import React, { useEffect, useMemo, useState } from "react";
import { X, Zap, Package, CreditCard, History, RefreshCw, Search, ImageIcon, Layers, Fingerprint, Plus, CheckCircle2, XCircle, Clock, Loader2, TrendingUp } from "lucide-react";
import { NeonButton, accentColor } from "@/components/Holo";
import api from "@/lib/api";
import { abbrevNum, fmtDate } from "@/lib/format";
import { estImages } from "@/lib/billing";

const ACCENTS = { credits: "turquoise", renders: "pink", vault: "green", campaigns: "purple" };
const TITLES = { credits: "Credit Center", renders: "Render History", vault: "Vault Entries", campaigns: "Campaign Dashboard" };

const Mini = ({ label, value, hex }) => (
  <div className="border border-white/10 p-3 text-center"><div className="font-display font-black text-xl truncate" style={{ color: hex }}>{value}</div><div className="font-mono-tech text-[9px] uppercase tracking-widest text-white/40 mt-1">{label}</div></div>
);

const Skeleton = () => (
  <div className="space-y-4 animate-pulse" data-testid="stat-loading">
    <div className="flex items-center justify-center gap-2 py-2">
      <Loader2 size={16} className="text-white/40 animate-spin" />
      <span className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">Loading…</span>
    </div>
    <div className="h-24 border border-white/10 bg-white/5" />
    <div className="grid grid-cols-3 gap-3">{[0, 1, 2].map((i) => <div key={i} className="h-16 border border-white/10 bg-white/5" />)}</div>
    <div className="space-y-2">{[0, 1, 2, 3].map((i) => <div key={i} className="h-9 border border-white/10 bg-white/5" />)}</div>
  </div>
);

const Empty = ({ text }) => <div className="font-mono-tech text-white/30 text-xs py-4 text-center">{text}</div>;

export default function StatDetail({ cardKey, user, setActive, onClose }) {
  const accent = ACCENTS[cardKey] || "turquoise";
  const c = accentColor(accent);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [usage, setUsage] = useState(null);
  const [gens, setGens] = useState([]);
  const [vault, setVault] = useState([]);
  const [camps, setCamps] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    let p = Promise.resolve();
    if (cardKey === "credits") {
      p = Promise.all([api.get("/credits/history"), api.get("/credits/usage")])
        .then(([h, u]) => { setHistory(h.data); setUsage(u.data); });
    } else if (cardKey === "renders") {
      p = api.get("/generations").then(({ data }) => setGens(data));
    } else if (cardKey === "vault") {
      p = Promise.all([api.get("/vault?kind=character"), api.get("/vault?kind=product"), api.get("/vault?kind=scene")])
        .then(([a, b, cc]) => setVault([...a.data.map(x => ({ ...x, kind: "character" })), ...b.data.map(x => ({ ...x, kind: "product" })), ...cc.data.map(x => ({ ...x, kind: "scene" }))]));
    } else if (cardKey === "campaigns") {
      p = api.get("/campaigns").then(({ data }) => setCamps(data));
    }
    p.catch((e) => console.error(e)).finally(() => setLoading(false));
  }, [cardKey]);

  const go = (section) => { onClose(); setActive(section); };

  const purchases = useMemo(() => history.filter(h => /purchase|pack|recharge|topup|subscription/i.test(h.type || "")), [history]);
  const renderStats = useMemo(() => {
    const total = gens.length;
    const completed = gens.filter(g => g.status === "completed").length;
    const failed = gens.filter(g => g.status === "failed" || g.refunded).length;
    const rate = total ? Math.round((completed / total) * 100) : 0;
    const last = gens[0]?.created_at;
    const counts = {};
    gens.forEach(g => { if (g.module) counts[g.module] = (counts[g.module] || 0) + 1; });
    const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0];
    return { total, completed, failed, rate, last, top };
  }, [gens]);

  const filteredGens = useMemo(() => gens.filter(g =>
    (filter === "all" || (filter === "failed" ? (g.status === "failed" || g.refunded) : g.status === filter)) &&
    (!q || (g.prompt || "").toLowerCase().includes(q.toLowerCase()) || (g.module || "").toLowerCase().includes(q.toLowerCase()))), [gens, q, filter]);
  const filteredVault = useMemo(() => vault.filter(v =>
    (filter === "all" || v.kind === filter) && (!q || (v.name || "").toLowerCase().includes(q.toLowerCase()))), [vault, q, filter]);
  const filteredCamps = useMemo(() => camps.filter(cp =>
    (filter === "all" || (cp.status || "draft") === filter) && (!q || (cp.name || "").toLowerCase().includes(q.toLowerCase()))), [camps, q, filter]);

  return (
    <div className="fixed inset-0 z-[75] bg-black/70 backdrop-blur-sm flex justify-end" onClick={onClose} data-testid="stat-panel">
      <div className="glass border-l w-full max-w-md h-full overflow-y-auto p-5 sm:p-6 animate-slide-in" style={{ borderColor: `rgba(${c.rgb},0.4)` }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display font-bold text-xl" style={{ color: c.hex }}>{TITLES[cardKey]}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-neon-pink transition-colors" data-testid="stat-panel-close"><X size={20} /></button>
        </div>

        {loading ? <Skeleton /> : (
          <>
            {cardKey === "credits" && (
              <div className="space-y-5" data-testid="detail-credits">
                <div className="text-center border p-6" style={{ borderColor: `rgba(${c.rgb},0.3)` }}>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/40">Current Balance</div>
                  <div className="font-display font-black text-3xl sm:text-4xl mt-1 tabular-nums break-all" style={{ color: c.hex }} data-testid="detail-credit-balance">{(user?.credits ?? 0).toLocaleString()}</div>
                  <div className="font-mono-tech text-[10px] text-white/40 mt-1">≈ {estImages(user?.credits)} generations</div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Mini label="Monthly" value={abbrevNum(usage?.credits_monthly ?? user?.monthly_credits ?? 0)} hex="#ff007f" />
                  <Mini label="Purchased" value={abbrevNum(usage?.credits_purchased ?? user?.purchased_credits ?? 0)} hex="#39ff14" />
                  <Mini label="Legacy" value={abbrevNum(usage?.credits_legacy ?? user?.legacy_credits ?? 0)} hex="#00f0ff" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Mini label="Credits Used" value={abbrevNum(usage?.credits_used ?? 0)} hex="#eaff00" />
                  <Mini label="Refunded" value={abbrevNum(usage?.credits_refunded ?? 0)} hex="#39ff14" />
                </div>
                {purchases.length > 0 && (
                  <div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 mb-2">Recent Purchases</div>
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {purchases.slice(0, 6).map(h => (<div key={h.id} className="flex justify-between border border-white/8 px-3 py-2 font-mono-tech text-[11px]"><span className="text-white/60">{fmtDate(h.created_at)}</span><span className="text-neon-green">+{h.amount}</span></div>))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-widest text-white/50 mb-2">Credit History</div>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {history.length === 0 ? <Empty text="No credit history yet." /> :
                      history.map(h => (<div key={h.id} className="flex justify-between border border-white/8 px-3 py-2 font-mono-tech text-[11px]"><span className="text-white/60">{(h.type || "").replace(/_/g, " ")}</span><span className={h.amount >= 0 ? "text-neon-green" : "text-neon-pink"}>{h.amount >= 0 ? "+" : ""}{h.amount}</span></div>))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <NeonButton accent="green" onClick={() => go("billing")} data-testid="detail-buy-credits"><Zap size={13} /> Purchase Credits</NeonButton>
                  <NeonButton accent="pink" onClick={() => go("billing")} data-testid="detail-billing"><CreditCard size={13} /> Billing</NeonButton>
                  <NeonButton accent="turquoise" onClick={() => go("account")} data-testid="detail-history"><History size={13} /> Full History</NeonButton>
                  <NeonButton accent="purple" onClick={() => go("billing")} data-testid="detail-autorecharge"><RefreshCw size={13} /> Auto-Recharge</NeonButton>
                </div>
              </div>
            )}

            {cardKey === "renders" && (
              <div className="space-y-4" data-testid="detail-renders">
                <div className="grid grid-cols-3 gap-3">
                  <Mini label="Total" value={renderStats.total} hex="#ff007f" />
                  <Mini label="Completed" value={renderStats.completed} hex="#39ff14" />
                  <Mini label="Failed" value={renderStats.failed} hex="#ff007f" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Mini label="Success Rate" value={`${renderStats.rate}%`} hex="#39ff14" />
                  <Mini label="Top Module" value={renderStats.top || "—"} hex="#00f0ff" />
                  <Mini label="Last Render" value={renderStats.last ? fmtDate(renderStats.last) : "—"} hex="#eaff00" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 border border-white/12 px-2"><Search size={13} className="text-white/40" /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search renders" data-testid="renders-search" className="bg-transparent py-2 text-white font-mono-tech text-xs w-full focus:outline-none" /></div>
                  <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="renders-filter" className="bg-black/40 border border-white/12 px-2 text-white font-mono-tech text-xs">
                    <option value="all" className="bg-[#0d0d0f]">All</option><option value="completed" className="bg-[#0d0d0f]">Completed</option><option value="failed" className="bg-[#0d0d0f]">Failed</option>
                  </select>
                </div>
                <div className="space-y-2 max-h-[45vh] overflow-y-auto">
                  {filteredGens.length === 0 ? <Empty text={gens.length === 0 ? "No renders yet." : "No renders match."} /> :
                    filteredGens.map(g => (
                      <div key={g.id} className="flex items-center gap-3 border border-white/8 p-2" data-testid={`render-${g.id}`}>
                        <div className="w-11 h-11 bg-black/40 overflow-hidden shrink-0 flex items-center justify-center">{g.image ? <img src={g.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={16} className="text-white/20" />}</div>
                        <div className="min-w-0 flex-1"><div className="font-mono-tech text-[11px] text-white/80 truncate">{g.prompt || g.module}</div><div className="font-mono-tech text-[9px] text-white/35 uppercase">{g.module} · {g.credits_used} cr · {fmtDate(g.created_at)}</div></div>
                        {g.status === "completed" ? <CheckCircle2 size={14} className="text-neon-green shrink-0" /> : <XCircle size={14} className="text-neon-pink shrink-0" />}
                      </div>))}
                </div>
                <NeonButton accent="pink" className="w-full" onClick={() => go("ai_lab")} data-testid="detail-open-lab"><TrendingUp size={13} /> Open AI Laboratory</NeonButton>
              </div>
            )}

            {cardKey === "vault" && (
              <div className="space-y-4" data-testid="detail-vault">
                <div className="grid grid-cols-3 gap-3">
                  <Mini label="Characters" value={vault.filter(v => v.kind === "character").length} hex="#ff007f" />
                  <Mini label="Products" value={vault.filter(v => v.kind === "product").length} hex="#00f0ff" />
                  <Mini label="Scenes" value={vault.filter(v => v.kind === "scene").length} hex="#39ff14" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 border border-white/12 px-2"><Search size={13} className="text-white/40" /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search vault" data-testid="vault-search" className="bg-transparent py-2 text-white font-mono-tech text-xs w-full focus:outline-none" /></div>
                  <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="vault-filter" className="bg-black/40 border border-white/12 px-2 text-white font-mono-tech text-xs">
                    <option value="all" className="bg-[#0d0d0f]">All</option><option value="character" className="bg-[#0d0d0f]">Characters</option><option value="product" className="bg-[#0d0d0f]">Products</option><option value="scene" className="bg-[#0d0d0f]">Scenes</option>
                  </select>
                </div>
                <div className="space-y-2 max-h-[48vh] overflow-y-auto">
                  {filteredVault.length === 0 ? <Empty text={vault.length === 0 ? "No vault entries yet." : "No entries match."} /> :
                    filteredVault.map(v => (
                      <div key={v.id} className="flex items-center gap-3 border border-white/8 p-2" data-testid={`vault-item-${v.id}`}>
                        <div className="w-10 h-10 bg-black/40 overflow-hidden shrink-0 flex items-center justify-center">{v.image ? <img src={v.image} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={14} className="text-white/20" />}</div>
                        <div className="min-w-0 flex-1"><div className="font-mono-tech text-[11px] text-white/80 truncate">{v.name}</div><div className="font-mono-tech text-[9px] text-white/35 uppercase">{v.kind} · {fmtDate(v.created_at)}</div></div>
                      </div>))}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <NeonButton accent="pink" onClick={() => go("identity")} data-testid="detail-vault-identity"><Fingerprint size={12} /> Identity</NeonButton>
                  <NeonButton accent="turquoise" onClick={() => go("brand")} data-testid="detail-vault-brand"><Package size={12} /> Brand</NeonButton>
                  <NeonButton accent="green" onClick={() => go("scene")} data-testid="detail-vault-scene"><Layers size={12} /> Scene</NeonButton>
                </div>
              </div>
            )}

            {cardKey === "campaigns" && (
              <div className="space-y-4" data-testid="detail-campaigns">
                <div className="grid grid-cols-3 gap-3">
                  <Mini label="Active" value={camps.filter(cp => cp.status === "active").length} hex="#39ff14" />
                  <Mini label="Draft" value={camps.filter(cp => (cp.status || "draft") === "draft").length} hex="#eaff00" />
                  <Mini label="Completed" value={camps.filter(cp => cp.status === "completed").length} hex="#00f0ff" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center gap-2 border border-white/12 px-2"><Search size={13} className="text-white/40" /><input value={q} onChange={e => setQ(e.target.value)} placeholder="Search campaigns" data-testid="campaigns-search" className="bg-transparent py-2 text-white font-mono-tech text-xs w-full focus:outline-none" /></div>
                  <select value={filter} onChange={e => setFilter(e.target.value)} data-testid="campaigns-filter" className="bg-black/40 border border-white/12 px-2 text-white font-mono-tech text-xs">
                    <option value="all" className="bg-[#0d0d0f]">All</option><option value="active" className="bg-[#0d0d0f]">Active</option><option value="draft" className="bg-[#0d0d0f]">Draft</option><option value="completed" className="bg-[#0d0d0f]">Completed</option>
                  </select>
                </div>
                <div className="space-y-2 max-h-[48vh] overflow-y-auto">
                  {filteredCamps.length === 0 ? <Empty text={camps.length === 0 ? "No campaigns yet." : "No campaigns match."} /> :
                    filteredCamps.map(cp => (
                      <button key={cp.id} onClick={() => go("worldforge")} className="w-full flex items-center justify-between border border-white/8 p-3 hover:border-white/25 transition-colors text-left" data-testid={`campaign-${cp.id}`}>
                        <div className="min-w-0"><div className="font-mono-tech text-[12px] text-white/85 truncate">{cp.name}</div><div className="font-mono-tech text-[9px] text-white/35 uppercase">{cp.shot_count} shots · {cp.status || "draft"} · {fmtDate(cp.created_at)}</div></div>
                        <Clock size={13} className="text-white/30 shrink-0" />
                      </button>))}
                </div>
                <NeonButton accent="purple" className="w-full" onClick={() => go("worldforge")} data-testid="detail-new-campaign"><Plus size={13} /> Create New Campaign</NeonButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
